/* The trial editor is an IDE surface: it stays dark in both colour schemes,
   the way an editor does. These locals are deliberately not theme tokens. */
.exercise {
  --ex-bg:    #1a1a1a;
  --ex-chrome:#111111;
  --ex-panel: #0d0d0d;
  --ex-well:  #080808;
  --ex-edit:  #151515;
  --ex-fg:    #f4f4f2;
  --ex-dim:   rgba(244, 244, 242, .45);
  --ex-faint: rgba(244, 244, 242, .22);
  --ex-rule:  rgba(255, 255, 255, .07);
  background: var(--ex-bg);
  color: var(--ex-fg);
}

.bar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 14px; padding: 16px 24px;
  background: var(--ex-chrome); border-bottom: 1px solid var(--ex-rule);
}
.barLeft { display: flex; align-items: center; gap: 14px; }
.lamp { width: 10px; height: 10px; border-radius: 2px; background: #ffd633; box-shadow: 0 0 12px #ffc400; }
.barTitle { margin-top: 3px; font: 400 18px var(--serif); color: var(--ex-fg); }
.barRight { display: flex; align-items: center; gap: 20px; }
.pips { display: flex; gap: 5px; }
.pip { display: block; width: 22px; height: 4px; border-radius: 2px; background: rgba(244,244,242,.18); transition: background-color .2s ease; }
.pip.pass { background: #ffc400; }
.pip.fail { background: #ff6b2c; }

.grid { display: grid; grid-template-columns: 300px 1fr 348px; min-height: 520px;
        grid-template-areas: "brief editor console"; }
.grid > * { min-width: 0; }
.brief { grid-area: brief; }
.editorPane { grid-area: editor; }
.console { grid-area: console; }

/* practice: two columns inside the lesson's practice card, the brief topping
   the right one, so the editor gets what the brief's own column used to take.
   The card stays the lesson's width: widening it to give the editor more made
   every question span the page (Nick, 2026-10-08). */
.practice .grid { grid-template-columns: minmax(0, 1fr) 340px; grid-template-rows: auto 1fr;
                  grid-template-areas: "editor brief" "editor console"; }
.practice .brief { border-right: none; border-left: 1px solid var(--ex-rule); background: var(--ex-panel); padding-bottom: 4px; }
.practice .console { padding-top: 18px; }

/* Brief ------------------------------------------------------------------ */
.brief { padding: 24px 22px; border-right: 1px solid var(--ex-rule); }
.briefText { margin: 10px 0 0; font: 400 13.5px/1.7 var(--sans); color: rgba(244,244,242,.72); }
.briefText code { font: 500 12.5px var(--mono); background: none; color: #ffd633; padding: 0; }
.briefText strong { color: var(--ex-fg); }
.sectionGap { margin-top: 24px; }

.trials { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.trial {
  display: flex; gap: 10px; align-items: flex-start; padding: 11px 12px;
  border: 1px solid rgba(244,244,242,.09); border-radius: var(--radius);
  background: rgba(244,244,242,.02);
  transition: border-color .2s ease;
}
.trial.pass { border-color: rgba(255,196,0,.4); }
.trial.fail { border-color: rgba(255,107,44,.4); }
.trialMark { font: 600 12px var(--mono); color: rgba(244,244,242,.35); }
.trial.pass .trialMark { color: #ffd633; }
.trial.fail .trialMark { color: #ff6b2c; }
.trialLabel { flex: 1; font: 400 11.5px/1.5 var(--mono); color: rgba(244,244,242,.7); }

.hint { margin-top: 24px; padding: 13px 14px; border: 1px dashed rgba(255,107,44,.45); border-radius: var(--radius); }
.hintToggle {
  border: none; background: none; padding: 0; cursor: pointer;
  font: 500 10px var(--mono); letter-spacing: .16em; text-transform: uppercase; color: #ff6b2c;
}
.hintBody { margin: 7px 0 0; font: 400 12px/1.55 var(--sans); color: rgba(244,244,242,.55); }

/* Editor ----------------------------------------------------------------- */
.editorPane { display: flex; flex-direction: column; background: var(--ex-edit); min-width: 0; }
.tabs { display: flex; gap: 2px; padding: 0 14px; background: var(--ex-panel); border-bottom: 1px solid var(--ex-rule); }
.tab { padding: 11px 16px; font: 400 11.5px var(--mono); color: #ffd633; border-bottom: 2px solid #ffd633; }

.editor { flex: 1; display: flex; min-height: 300px; position: relative; }
.gutter {
  padding: 18px 10px 18px 18px; text-align: right; user-select: none;
  font: 400 13.5px/1.85 var(--mono); color: var(--ex-faint);
}
.editorBox { flex: 1; position: relative; min-width: 0; }
.layer, .input {
  margin: 0; border: none; white-space: pre; overflow-wrap: normal;
  font: 400 13.5px/1.85 var(--mono); padding: 18px 18px 18px 8px;
}
.layer { position: absolute; inset: 0; color: #eaeaea; pointer-events: none; overflow: hidden; }
.input {
  position: relative; width: 100%; height: 100%; resize: none; outline: none;
  background: transparent; color: transparent; caret-color: #ffd633; overflow: auto;
}
.input:focus-visible { outline: 2px solid #ffc400; outline-offset: -2px; }

.editorFoot {
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 14px 18px; background: var(--ex-panel); border-top: 1px solid var(--ex-rule);
}
.runtime { font: 400 11px var(--mono); color: var(--ex-dim); }
.runActions { display: flex; gap: 10px; }

/* Console ---------------------------------------------------------------- */
.console { padding: 22px; background: var(--ex-panel); border-left: 1px solid var(--ex-rule); display: flex; flex-direction: column; }
.well {
  margin-top: 12px; flex: 1; min-height: 160px; padding: 14px;
  border-radius: var(--radius); background: var(--ex-well);
  border: 1px solid rgba(244,244,242,.06);
  font: 400 12px/1.9 var(--mono); overflow: auto;
}
.line { white-space: pre-wrap; color: var(--ex-dim); }
.line.ok  { color: #ffd633; }
.line.err { color: #ff6b2c; }

.verdict {
  margin-top: 16px; padding: 16px; border-radius: var(--radius);
  border: 1px solid rgba(244,244,242,.12); color: rgba(244,244,242,.5);
  transition: border-color .2s ease, background-color .2s ease, color .2s ease;
}
.verdict.pass { border-color: rgba(255,196,0,.5); background: rgba(255,196,0,.12); color: #ffd633; }
.verdict.fail { border-color: rgba(255,107,44,.4); color: #ff6b2c; }
.verdictTitle { font: 600 10.5px var(--mono); letter-spacing: .18em; }
.verdictBody { margin: 8px 0 0; font: 400 12.5px/1.6 var(--sans); }

@media (max-width: 1000px) {
  .grid, .practice .grid { grid-template-columns: 1fr; grid-template-rows: none; grid-template-areas: none; }
  .grid > * { grid-area: auto; }
  .practice .brief { border-left: none; }
  .brief { border-right: none; border-bottom: 1px solid var(--ex-rule); }
  .console { border-left: none; border-top: 1px solid var(--ex-rule); }
}
